/**
 * useScenarioFormRHF - React Hook Form based scenario form management
 *
 * Replaces the 50+ useState calls with centralized React Hook Form state.
 * Provides Zod validation, dirty tracking, and field-level error handling.
 *
 * Usage:
 * ```jsx
 * // In ScenarioEditor.js
 * const { methods, formState, ... } = useScenarioFormRHF();
 *
 * return (
 *   <FormProvider {...methods}>
 *     <SiteConfiguration />
 *     <SolarConfiguration />
 *   </FormProvider>
 * );
 *
 * // In child components
 * const { watch, setValue, formState } = useFormContext();
 * const latitude = watch('site.latitude');
 * ```
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useForm, useFormContext as useRHFFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// Import centralized defaults
import { DEFAULT_PV_COST_PER_KW, DEFAULT_BATTERY_COST_PER_KWH, DEFAULT_ANNUAL_KWH, DEFAULT_FINANCIAL, FINANCIAL_DEFAULTS, TARIFF_DEFAULTS, GENERATOR_DEFAULTS, RESILIENCE_DEFAULTS } from '../config/defaults';

/**
 * UI Form Schema
 *
 * This schema is for the form UI, not the solver payload.
 * It validates user input and provides helpful error messages.
 * The solver payload is built separately from this form data.
 */
const SiteFormSchema = z.object({
  scenarioName: z.string().default(''),
  siteName: z.string().default(''),
  street: z.string().default(''),
  city: z.string().default(''),
  state: z.string().default(''),
  zipCode: z.string().default(''),
  siteDescription: z.string().default(''),
  facilityType: z.string().default('commercial'),
  utilityName: z.string().default(''),
  serviceClass: z.string().default('commercial'),
  offGrid: z.boolean().default(false),
  // Coordinates from geocoding
  latitude: z.number().nullable().default(null),
  longitude: z.number().nullable().default(null),
});

const SolarFormSchema = z.object({
  enabled: z.boolean().default(false),
  sizingMode: z.enum(['prescribed', 'optimized']).default('prescribed'),
  maxKw: z.string().default(''),
  minKw: z.string().default('0'),
  costPerKw: z.string().default(String(DEFAULT_PV_COST_PER_KW)),
  exportMode: z.enum(['none', 'net_metering', 'wholesale']).default('none'),
  // Existing solar
  existingKw: z.string().default(''),
  existingProfile: z.array(z.number()).optional(),
});

const BatteryFormSchema = z.object({
  enabled: z.boolean().default(false),
  sizingMode: z.enum(['prescribed', 'optimized']).default('prescribed'),
  maxKw: z.string().default(''),
  maxKwh: z.string().default(''),
  minKw: z.string().default('0'),
  minKwh: z.string().default('0'),
  costPerKwh: z.string().default(String(DEFAULT_BATTERY_COST_PER_KWH)),
  reservePercent: z.string().default('20'),
  chargeSource: z.enum(['pv_only', 'grid_only', 'pv_and_grid']).default('pv_and_grid'),
  canGridCharge: z.boolean().default(true),
  // Multi-battery support
  batteries: z.array(z.object({
    id: z.number(),
    name: z.string(),
    kw: z.string(),
    kwh: z.string(),
  })).default([{ id: 1, name: 'Battery 1', kw: '', kwh: '' }]),
});

const GeneratorFormSchema = z.object({
  enabled: z.boolean().default(false),
  sizingMode: z.enum(['prescribed', 'optimized']).default('prescribed'),
  maxKw: z.string().default(''),
  minKw: z.string().default('0'),
  fuelType: z.enum(['diesel', 'natural_gas', 'propane']).default('diesel'),
  fuelCostPerGallon: z.string().default('3'),
  fuelCostPerMmbtu: z.string().default('6'),
  exportMode: z.enum(['none', 'wholesale']).default('none'),
  runHoursLimit: z.string().default(''),
  uptimePercent: z.string().default('96.5'),
  thermalEfficiency: z.string().default(''),
  // Multi-generator support
  generators: z.array(z.object({
    id: z.number(),
    name: z.string(),
    kw: z.string(),
    fuelType: z.string(),
    databaseId: z.string().optional(),
    manufacturer: z.string().optional(),
    model: z.string().optional(),
    efficiency: z.number().optional(),
  })).default([]),
});

const TariffFormSchema = z.object({
  // Tariff source
  source: z.enum(['blended', 'tou', 'approved', 'custom']).default('blended'),
  tariffId: z.string().nullable().default(null),

  // Blended rates
  blendedEnergyRate: z.string().default('0.12'),
  blendedDemandRate: z.string().default('15.0'),
  monthlyFixedCharge: z.string().default('25.0'),

  // TOU data (for custom TOU builder)
  touData: z.object({
    periods: z.array(z.any()).optional(),
    windows: z.array(z.any()).optional(),
    schedule: z.any().optional(),
    fixedMonthlyFee: z.string().optional(),
    facilityDemand: z.string().optional(),
  }).optional(),

  // Selected tariff data (from database)
  selectedTariffData: z.any().nullable().default(null),

  // Export/NEM configuration
  exportConfig: z.object({
    enabled: z.boolean().default(false),
    mode: z.enum(['none', 'hourly', 'match_import', 'fixed']).default('none'),
    rate: z.string().default(''),
    tariffId: z.string().nullable().default(null),
    tariffData: z.any().nullable().default(null),
  }).default({}),

  // CA DER charges
  caDerConfig: z.any().nullable().default(null),

  // Gas tariff
  gasTariffId: z.string().nullable().default(null),
  gasTariffData: z.any().nullable().default(null),
});

const UIStateFormSchema = z.object({
  inputMode: z.enum(['ai_assisted', 'manual']).default('ai_assisted'),
  aiExtractionComplete: z.boolean().default(false),
  aiExtractedData: z.any().nullable().default(null),
  calculationMethod: z.enum(['reopt', 'simple']).default('reopt'),
  activeTab: z.number().default(0),
});

/**
 * Complete Form Schema
 */
export const ScenarioFormSchema = z.object({
  site: SiteFormSchema.default({}),
  solar: SolarFormSchema.default({}),
  battery: BatteryFormSchema.default({}),
  generator: GeneratorFormSchema.default({}),
  tariff: TariffFormSchema.default({}),
  ui: UIStateFormSchema.default({}),
  // Commercial assumptions and deal analysis (complex objects)
  commercialAssumptions: z.any().nullable().default(null),
  dealAnalysisConfig: z.any().nullable().default(null),
});

/**
 * Default form values
 */
const defaultValues = {
  site: {
    scenarioName: '',
    siteName: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    siteDescription: '',
    facilityType: 'commercial',
    utilityName: '',
    serviceClass: 'commercial',
    offGrid: false,
    latitude: null,
    longitude: null,
  },
  solar: {
    enabled: false,
    sizingMode: 'prescribed',
    maxKw: '',
    minKw: '0',
    costPerKw: String(DEFAULT_PV_COST_PER_KW),
    exportMode: 'none',
    existingKw: '',
  },
  battery: {
    enabled: false,
    sizingMode: 'prescribed',
    maxKw: '',
    maxKwh: '',
    minKw: '0',
    minKwh: '0',
    costPerKwh: String(DEFAULT_BATTERY_COST_PER_KWH),
    reservePercent: '20',
    chargeSource: 'pv_and_grid',
    canGridCharge: true,
    batteries: [{ id: 1, name: 'Battery 1', kw: '', kwh: '' }],
  },
  generator: {
    enabled: false,
    sizingMode: 'prescribed',
    maxKw: '',
    minKw: '0',
    fuelType: 'diesel',
    fuelCostPerGallon: '3',
    fuelCostPerMmbtu: '6',
    exportMode: 'none',
    runHoursLimit: '',
    uptimePercent: '96.5',
    thermalEfficiency: '',
    generators: [],
  },
  tariff: {
    source: 'blended',
    tariffId: null,
    blendedEnergyRate: '0.12',
    blendedDemandRate: '15.0',
    monthlyFixedCharge: '25.0',
    touData: null,
    selectedTariffData: null,
    exportConfig: {
      enabled: false,
      mode: 'none',
      rate: '',
      tariffId: null,
      tariffData: null,
    },
    caDerConfig: null,
    gasTariffId: null,
    gasTariffData: null,
  },
  ui: {
    inputMode: 'ai_assisted',
    aiExtractionComplete: false,
    aiExtractedData: null,
    calculationMethod: 'reopt',
    activeTab: 0,
  },
  commercialAssumptions: null,
  dealAnalysisConfig: null,
};

/**
 * useScenarioFormRHF - Main hook for scenario form state
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.initialData - Initial form data (from loaded scenario)
 * @param {Function} options.onValidationError - Callback for validation errors
 * @returns {Object} Form methods and state
 */
export function useScenarioFormRHF(options = {}) {
  const { initialData, onValidationError } = options;

  // Initialize React Hook Form
  const methods = useForm({
    resolver: zodResolver(ScenarioFormSchema),
    defaultValues: initialData || defaultValues,
    mode: 'onChange', // Validate on change for immediate feedback
    reValidateMode: 'onChange',
  });

  const {
    watch,
    setValue,
    getValues,
    reset,
    handleSubmit,
    formState: { errors, isDirty, isValid, dirtyFields, touchedFields },
    trigger,
    clearErrors,
    setError,
  } = methods;

  // Watch specific sections for memoized access
  const siteValues = watch('site');
  const solarValues = watch('solar');
  const batteryValues = watch('battery');
  const generatorValues = watch('generator');
  const tariffValues = watch('tariff');
  const uiValues = watch('ui');

  /**
   * Load saved scenario data into the form
   */
  const loadScenarioData = useCallback((savedData) => {
    if (!savedData) return;

    // Map from legacy format to new form structure
    const formData = mapLegacyToFormData(savedData);
    reset(formData, { keepDefaultValues: false });
  }, [reset]);

  /**
   * Get form data in the format expected by the save function
   */
  const getFormDataForSave = useCallback(() => {
    const values = getValues();
    return mapFormDataToLegacy(values);
  }, [getValues]);

  /**
   * Reset specific sections
   */
  const resetSection = useCallback((section) => {
    switch (section) {
      case 'solar':
        reset({ ...getValues(), solar: defaultValues.solar });
        break;
      case 'battery':
        reset({ ...getValues(), battery: defaultValues.battery });
        break;
      case 'generator':
        reset({ ...getValues(), generator: defaultValues.generator });
        break;
      case 'tariff':
        reset({ ...getValues(), tariff: defaultValues.tariff });
        break;
      default:
        break;
    }
  }, [reset, getValues]);

  /**
   * Reset entire form
   */
  const resetAll = useCallback(() => {
    reset(defaultValues);
  }, [reset]);

  /**
   * Validate a specific section
   */
  const validateSection = useCallback(async (section) => {
    const result = await trigger(section);
    return result;
  }, [trigger]);

  /**
   * Get section-specific errors
   */
  const getSectionErrors = useCallback((section) => {
    return errors[section] || {};
  }, [errors]);

  /**
   * Check if section has errors
   */
  const sectionHasErrors = useCallback((section) => {
    return !!errors[section] && Object.keys(errors[section]).length > 0;
  }, [errors]);

  /**
   * Check if section is dirty (has unsaved changes)
   */
  const sectionIsDirty = useCallback((section) => {
    return !!dirtyFields[section];
  }, [dirtyFields]);

  // Backward compatibility: Create setter functions that match old API
  const legacySetters = useMemo(() => createLegacySetters(setValue), [setValue]);

  return {
    // React Hook Form methods (for FormProvider)
    methods,

    // Form state
    formState: {
      errors,
      isDirty,
      isValid,
      dirtyFields,
      touchedFields,
    },

    // Watched values (reactive)
    site: siteValues,
    solar: solarValues,
    battery: batteryValues,
    generator: generatorValues,
    tariff: tariffValues,
    ui: uiValues,

    // Core methods
    watch,
    setValue,
    getValues,
    reset,
    handleSubmit,
    trigger,
    clearErrors,
    setError,

    // Custom methods
    loadScenarioData,
    getFormDataForSave,
    resetSection,
    resetAll,
    validateSection,
    getSectionErrors,
    sectionHasErrors,
    sectionIsDirty,

    // Backward compatibility setters
    ...legacySetters,

    // Constants
    DEFAULT_PV_COST_PER_KW,
    DEFAULT_BATTERY_COST_PER_KWH,
    DEFAULT_ANNUAL_KWH,
    DEFAULT_FINANCIAL,
  };
}

/**
 * Create legacy setter functions for backward compatibility
 * These allow gradual migration of components
 */
function createLegacySetters(setValue) {
  return {
    // Site setters
    setScenarioName: (v) => setValue('site.scenarioName', v),
    setSiteName: (v) => setValue('site.siteName', v),
    setStreet: (v) => setValue('site.street', v),
    setCity: (v) => setValue('site.city', v),
    setState: (v) => setValue('site.state', v),
    setZipCode: (v) => setValue('site.zipCode', v),
    setSiteDescription: (v) => setValue('site.siteDescription', v),
    setFacilityType: (v) => setValue('site.facilityType', v),
    setUtilityName: (v) => setValue('site.utilityName', v),
    setServiceClass: (v) => setValue('site.serviceClass', v),
    setOffGrid: (v) => setValue('site.offGrid', v),
    setLatitude: (v) => setValue('site.latitude', v),
    setLongitude: (v) => setValue('site.longitude', v),

    // Solar setters
    setUsePv: (v) => setValue('solar.enabled', v),
    setSizingMode: (v) => setValue('solar.sizingMode', v),
    setPvMaxKw: (v) => setValue('solar.maxKw', v),
    setPvCost: (v) => setValue('solar.costPerKw', v),
    setPvExportMode: (v) => setValue('solar.exportMode', v),

    // Battery setters
    setUseStorage: (v) => setValue('battery.enabled', v),
    setBatterySizingMode: (v) => setValue('battery.sizingMode', v),
    setStorageMaxKw: (v) => setValue('battery.maxKw', v),
    setStorageMaxKwh: (v) => setValue('battery.maxKwh', v),
    setBatteryReservePercent: (v) => setValue('battery.reservePercent', v),
    setBessChargeSource: (v) => setValue('battery.chargeSource', v),
    setBatteries: (v) => setValue('battery.batteries', v),

    // Generator setters
    setUseGenerator: (v) => setValue('generator.enabled', v),
    setGeneratorSizingMode: (v) => setValue('generator.sizingMode', v),
    setGeneratorMaxKw: (v) => setValue('generator.maxKw', v),
    setGeneratorFuelType: (v) => setValue('generator.fuelType', v),
    setGeneratorFuelCostPerGallon: (v) => setValue('generator.fuelCostPerGallon', v),
    setGeneratorFuelCostPerMmbtu: (v) => setValue('generator.fuelCostPerMmbtu', v),
    setGeneratorExportMode: (v) => setValue('generator.exportMode', v),
    setGeneratorRunHoursLimit: (v) => setValue('generator.runHoursLimit', v),
    setGeneratorUptimePercent: (v) => setValue('generator.uptimePercent', v),
    setGenerators: (v) => setValue('generator.generators', v),

    // Tariff setters
    setBlendedEnergyRate: (v) => setValue('tariff.blendedEnergyRate', v),
    setBlendedDemandRate: (v) => setValue('tariff.blendedDemandRate', v),
    setMonthlyFixedCharge: (v) => setValue('tariff.monthlyFixedCharge', v),
    setSelectedTariffData: (v) => setValue('tariff.selectedTariffData', v),
    setExportConfig: (v) => setValue('tariff.exportConfig', v),
    setCaDerConfig: (v) => setValue('tariff.caDerConfig', v),
    setGasTariffId: (v) => setValue('tariff.gasTariffId', v),
    setGasTariffData: (v) => setValue('tariff.gasTariffData', v),

    // UI setters
    setInputMode: (v) => setValue('ui.inputMode', v),
    setAiExtractionComplete: (v) => setValue('ui.aiExtractionComplete', v),
    setAiExtractedData: (v) => setValue('ui.aiExtractedData', v),
    setCalculationMethod: (v) => setValue('ui.calculationMethod', v),
    setTab: (v) => setValue('ui.activeTab', v),

    // Other setters
    setCommercialAssumptions: (v) => setValue('commercialAssumptions', v),
    setDealAnalysisConfig: (v) => setValue('dealAnalysisConfig', v),
  };
}

/**
 * Map legacy saved data format to new form structure
 */
function mapLegacyToFormData(savedData) {
  if (!savedData) return defaultValues;

  const { site, solar, battery, generator, tariff } = savedData;

  return {
    site: {
      scenarioName: site?.scenarioName || '',
      siteName: site?.siteName || '',
      street: site?.street || site?.siteAddress || '',
      city: site?.city || '',
      state: site?.state || '',
      zipCode: site?.zipCode || site?.location || '',
      siteDescription: site?.siteDescription || '',
      facilityType: site?.facilityType || 'commercial',
      utilityName: site?.utilityName || '',
      serviceClass: site?.serviceClass || 'commercial',
      offGrid: site?.offGrid || false,
      latitude: site?.latitude || null,
      longitude: site?.longitude || null,
    },
    solar: {
      enabled: solar?.usePv || false,
      sizingMode: solar?.sizingMode || 'prescribed',
      maxKw: solar?.pvMaxKw || '',
      minKw: solar?.pvMinKw || '0',
      costPerKw: solar?.pvCost || String(DEFAULT_PV_COST_PER_KW),
      exportMode: solar?.pvExportMode || 'none',
      existingKw: solar?.existingKw || '',
    },
    battery: {
      enabled: battery?.useStorage || false,
      sizingMode: battery?.batterySizingMode || 'prescribed',
      maxKw: battery?.storageMaxKw || '',
      maxKwh: battery?.storageMaxKwh || '',
      minKw: battery?.storageMinKw || '0',
      minKwh: battery?.storageMinKwh || '0',
      costPerKwh: battery?.storageCost || String(DEFAULT_BATTERY_COST_PER_KWH),
      reservePercent: battery?.batteryReservePercent || '20',
      chargeSource: battery?.bessChargeSource || 'pv_and_grid',
      canGridCharge: battery?.canGridCharge !== false,
      batteries: battery?.batteries || [{ id: 1, name: 'Battery 1', kw: '', kwh: '' }],
    },
    generator: {
      enabled: generator?.useGenerator || false,
      sizingMode: generator?.generatorSizingMode || 'prescribed',
      maxKw: generator?.generatorMaxKw || '',
      minKw: generator?.generatorMinKw || '0',
      fuelType: generator?.generatorFuelType || 'diesel',
      fuelCostPerGallon: generator?.generatorFuelCostPerGallon || '3',
      fuelCostPerMmbtu: generator?.generatorFuelCostPerMmbtu || '6',
      exportMode: generator?.generatorExportMode || 'none',
      runHoursLimit: generator?.generatorRunHoursLimit || '',
      uptimePercent: generator?.generatorUptimePercent || '96.5',
      thermalEfficiency: generator?.generatorThermalEfficiency || '',
      generators: generator?.generators || [],
    },
    tariff: {
      source: tariff?.rateStructureType || tariff?.source || 'blended',
      tariffId: tariff?.tariffId || tariff?.customTariffData?.id || null,
      blendedEnergyRate: tariff?.blendedEnergyRate || '0.12',
      blendedDemandRate: tariff?.blendedDemandRate || '15.0',
      monthlyFixedCharge: tariff?.monthlyFixedCharge || '25.0',
      touData: tariff?.touData || null,
      selectedTariffData: tariff?.selectedTariffData || null,
      exportConfig: tariff?.exportConfig || {
        enabled: false,
        mode: 'none',
        rate: '',
        tariffId: null,
        tariffData: null,
      },
      caDerConfig: tariff?.caDerConfig || null,
      gasTariffId: generator?.gasTariffId || null,
      gasTariffData: generator?.gasTariffData || null,
    },
    ui: {
      inputMode: savedData.inputMode || 'ai_assisted',
      aiExtractionComplete: savedData.aiExtractionComplete || false,
      aiExtractedData: savedData.aiExtractedData || null,
      calculationMethod: savedData.calculationMethod || 'reopt',
      activeTab: 0,
    },
    commercialAssumptions: savedData.commercialAssumptions || null,
    dealAnalysisConfig: savedData.dealAnalysisConfig || null,
  };
}

/**
 * Map form data back to legacy format for saving
 */
function mapFormDataToLegacy(formData) {
  return {
    site: {
      scenarioName: formData.site.scenarioName,
      siteName: formData.site.siteName,
      street: formData.site.street,
      city: formData.site.city,
      state: formData.site.state,
      zipCode: formData.site.zipCode,
      siteDescription: formData.site.siteDescription,
      facilityType: formData.site.facilityType,
      utilityName: formData.site.utilityName,
      serviceClass: formData.site.serviceClass,
      offGrid: formData.site.offGrid,
      latitude: formData.site.latitude,
      longitude: formData.site.longitude,
    },
    solar: {
      usePv: formData.solar.enabled,
      sizingMode: formData.solar.sizingMode,
      pvMaxKw: formData.solar.maxKw,
      pvMinKw: formData.solar.minKw,
      pvCost: formData.solar.costPerKw,
      pvExportMode: formData.solar.exportMode,
      existingKw: formData.solar.existingKw,
    },
    battery: {
      useStorage: formData.battery.enabled,
      batterySizingMode: formData.battery.sizingMode,
      storageMaxKw: formData.battery.maxKw,
      storageMaxKwh: formData.battery.maxKwh,
      storageMinKw: formData.battery.minKw,
      storageMinKwh: formData.battery.minKwh,
      batteryReservePercent: formData.battery.reservePercent,
      bessChargeSource: formData.battery.chargeSource,
      canGridCharge: formData.battery.canGridCharge,
      batteries: formData.battery.batteries,
    },
    generator: {
      useGenerator: formData.generator.enabled,
      generatorSizingMode: formData.generator.sizingMode,
      generatorMaxKw: formData.generator.maxKw,
      generatorMinKw: formData.generator.minKw,
      generatorFuelType: formData.generator.fuelType,
      generatorFuelCostPerGallon: formData.generator.fuelCostPerGallon,
      generatorFuelCostPerMmbtu: formData.generator.fuelCostPerMmbtu,
      generatorExportMode: formData.generator.exportMode,
      generatorRunHoursLimit: formData.generator.runHoursLimit,
      generatorUptimePercent: formData.generator.uptimePercent,
      generatorThermalEfficiency: formData.generator.thermalEfficiency,
      generators: formData.generator.generators,
      gasTariffId: formData.tariff.gasTariffId,
      gasTariffData: formData.tariff.gasTariffData,
    },
    tariff: {
      rateStructureType: formData.tariff.source,
      source: formData.tariff.source,
      tariffId: formData.tariff.tariffId,
      blendedEnergyRate: formData.tariff.blendedEnergyRate,
      blendedDemandRate: formData.tariff.blendedDemandRate,
      monthlyFixedCharge: formData.tariff.monthlyFixedCharge,
      touData: formData.tariff.touData,
      selectedTariffData: formData.tariff.selectedTariffData,
      exportConfig: formData.tariff.exportConfig,
      caDerConfig: formData.tariff.caDerConfig,
    },
    inputMode: formData.ui.inputMode,
    aiExtractionComplete: formData.ui.aiExtractionComplete,
    aiExtractedData: formData.ui.aiExtractedData,
    calculationMethod: formData.ui.calculationMethod,
    commercialAssumptions: formData.commercialAssumptions,
    dealAnalysisConfig: formData.dealAnalysisConfig,
  };
}

/**
 * Re-export useFormContext for child components
 * This allows them to access form state without prop drilling
 */
export const useFormContext = useRHFFormContext;

// ScenarioFormSchema is already exported inline above

export default useScenarioFormRHF;
